"use client";

import React from "react";
import { motion } from "framer-motion";
import { CreditCard, Globe as Globe2, PackageCheck, Layers, Clock, Building2, SearchCode, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function InternationalConsiderations() {
  return <section id="internationalecommerceandb2bconsiderations" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-medium tracking-wide">
            Cross-Border Strategy
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-serif">
            International Ecommerce and B2B SEO
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Expanding into foreign markets requires fundamentally different technical and content architectures
            depending on whether you operate a transactional online store or manage complex business-to-business sales
            cycles. Here is how I address the distinct requirements of each model.
          </p>
        </motion.div>

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Subsection 1: Ecommerce SEO Across Borders */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }}>
            <Card className="h-full border border-border/80 shadow-xs hover:shadow-md transition-shadow duration-300 bg-card/60 backdrop-blur-xs flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2 mb-2 text-primary">
                    <Globe2 className="size-5" />
                    <span className="text-xs uppercase tracking-wider font-semibold">B2C & Retail</span>
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-serif text-foreground font-semibold">
                    <h3 className="text-2xl sm:text-3xl font-serif text-foreground font-semibold">
                      International Ecommerce SEO
                    </h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-2">
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    Transactional retail websites face complex catalogue, pricing, and fulfillment challenges when selling
                    abroad. My work in{" "}
                    <Link to="/EcommerceSeoPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                      Ecommerce SEO
                    </Link>{" "}
                    aligns technical infrastructure with regional consumer trust so overseas shoppers discover and buy.
                  </p>

                  <Separator />

                  <div className="space-y-5">
                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <CreditCard className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Currencies, VAT &amp; Local Payments</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          Currency selectors must be crawlable without generating duplicate parameter URLs. Displaying local
                          currencies, transparent VAT/tax information, and preferred local payment methods (such as iDEAL,
                          Klarna, or regional wallets) builds critical trust before checkout.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <PackageCheck className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Stock Availability &amp; Indexation</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          A central question for international retail is whether a product page should remain indexable in a
                          country if it cannot be shipped there. I structure shipping rules and regional availability
                          signals so search engines index pages where fulfillment is genuinely possible.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <Layers className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Google Shopping Feeds &amp; Landing Pages</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          Cross-border shopping feeds require accurate multi-currency pricing, shipping rates, and tax
                          attributes per market. Dedicated category and regional landing pages ensure organic search and
                          merchant surfaces work together cleanly.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2">
                <div className="p-4 rounded-lg bg-secondary/40 border border-border/60">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <strong className="text-foreground font-medium">Regional Catalogues:</strong> Where inventory differs
                    across borders, I structure category taxonomies to prevent out-of-region catalogue gaps and indexing
                    conflicts.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Subsection 2: International B2B and Lead-Generation SEO */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }}>
            <Card className="h-full border border-border/80 shadow-xs hover:shadow-md transition-shadow duration-300 bg-card/60 backdrop-blur-xs flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2 mb-2 text-primary">
                    <Building2 className="size-5" />
                    <span className="text-xs uppercase tracking-wider font-semibold">High-Value Pipelines</span>
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-serif text-foreground font-semibold">
                    <h3 className="text-2xl sm:text-3xl font-serif text-foreground font-semibold">
                      International B2B and Lead-Generation SEO
                    </h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-2">
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    Cross-border B2B purchases involve extended evaluation cycles and multiple stakeholders. My work in{" "}
                    <Link to="/B2BSeoLeadGenerationPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                      B2B SEO &amp; Lead Generation
                    </Link>{" "}
                    helps enterprise decision-makers find and evaluate your services across international markets.
                  </p>

                  <Separator />

                  <div className="space-y-5">
                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <Clock className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Long Sales Cycles &amp; Multiple Stakeholders</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          Overseas procurement teams, technical leads, and finance directors have different search criteria.
                          I build content journeys tailored to each evaluation stage, addressing regional technical
                          compliance, commercial terms, and implementation questions.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <Building2 className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Country-Specific Enquiry Forms &amp; CRM Tracking</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          Lead generation across borders requires regional enquiry forms, country-level CRM attribution,
                          localised lead magnets, and compliance with local privacy regulations such as GDPR in Europe.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 items-start">
                      <div className="rounded-md bg-secondary/80 p-2 text-foreground shrink-0 mt-0.5">
                        <SearchCode className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">Research-Heavy B2B Search Queries</h4>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          Enterprise buyers search using industry terminology specific to their country. I conduct native
                          keyword research to uncover the exact commercial and technical vocabulary used by local
                          decision-makers.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2">
                <div className="p-4 rounded-lg bg-secondary/40 border border-border/60 flex items-start gap-3">
                  <Sparkles className="size-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Alongside traditional search engines, building visibility across AI search platforms in each market
                    is increasingly relevant for B2B discovery, as covered in my{" "}
                    <Link to="/AISearchOptimisationPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80">
                      AI Search Optimisation
                    </Link>{" "}
                    service.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}